<template>
    <div
        class="list-tile pointer"
        :class="{ chosen: checked }"
        data-testid="checkbox-list-element"
    >
        <div class="grid">
            <label
                class="input-select-item"
                :class="{ active: checked, disabled }"
                data-testid="checkbox-list-check"
            >
                <input
                    type="radio"
                    :checked="checked"
                    :disabled="disabled"
                    @change="handleClick"
                />
            </label>
            <Position
                x="start"
                y="center"
            >
                <span
                    class="list-tile-label-wrapper"
                    @click="handleClick"
                >
                    <Words
                        class="list-tile-label"
                        title
                    >
                        {{ item.label }}
                    </Words>
                </span>
            </Position>
            <Position
                v-if="item.descriptions?.length"
                x="end"
                y="center"
                class="arrow"
                :class="{ isToggled: toggled }"
                data-testid="checkbox-list-toggle"
            >
                <button
                    type="button"
                    :aria-expanded="toggled"
                    :aria-controls="`desc-${item.index}`"
                    data-testid="checkbox-list-toggle"
                    @click="toggle"
                    @keydown="handleToggleKeydown"
                >
                    <i class="mi-chevron-down" />
                    <span class="visually-hidden">
                        {{ toggled ? t('hide') : t('expand') }}
                    </span>
                </button>
            </Position>
        </div>
        <transition
            v-if="item.descriptions?.length"
            name="list-toggle"
            @enter="onEnter"
            @after-enter="onLeave"
            @before-leave="onEnter"
            @after-leave="onLeave"
        >
            <div
                v-if="toggled"
                class="list-tile-content"
                data-testid="checkbox-list-description"
            >
                <ul>
                    <li
                        v-for="(description, index) in item.descriptions"
                        :key="index"
                    >
                        <Tooltip
                            v-if="description.tooltip?.value"
                            :tip="description.tooltip?.value"
                            :placement="description.tooltip?.placement"
                        >
                            <div>
                                <strong>{{ description.label }}</strong>
                                <span>{{ description.value }}</span>
                            </div>
                        </Tooltip>
                        <div v-else>
                            <strong>{{ description.label }}</strong>
                            <span>{{ description.value }}</span>
                        </div>
                    </li>
                </ul>
            </div>
        </transition>
    </div>
</template>

<script lang="ts" setup>
import Position from 'widget-components/components/Position/Position.vue'
import Words from 'widget-components/components/Words/Words.vue'
import Tooltip from 'widget-components/atoms/Tooltip/Tooltip.vue'
import { ref } from 'vue'
import type { ListTileItem } from './types'
import { useI18n } from 'widget-components/utilities/i18n'

const { t } = useI18n()

const props = defineProps<{
    item: ListTileItem
    disabled?: boolean
    expanded?: boolean
}>()

const checked = defineModel<boolean>({ default: false })
const toggled = ref(props.expanded)

const toggle = () => {
    toggled.value = !toggled.value
}

const handleClick = () => {
    if (props.disabled) {
        return
    }
    checked.value = !checked.value
}

const onEnter = (el: Element) => {
    (el as HTMLElement).style.height = el.scrollHeight + 'px'
}

const onLeave = (el: Element) => {
    (el as HTMLElement).style.height = ''
}

const handleToggleKeydown = (event: KeyboardEvent) => {
    if (!['Enter', ' '].includes(event.key)) return

    event.preventDefault()
    event.stopPropagation()

    toggled.value = !toggled.value
}
</script>

<style lang="sass" scoped>
@import './InputCheckboxListTile.sass'
</style>
